<script setup lang="ts">
import Icon from "@/core/components/icon/Icon.vue";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import SurveyBuiltAnswer from "@/survey/components/SurveyBuiltAnswer.vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import {CheckInputSurveyQuestion, Survey, SurveySection} from "@/core/interfaces/survey/Survey";
import {computed} from "vue";
import {useInterviewStore} from "@/stores/interview-store";
import {storeToRefs} from "pinia";
import {QuestionType} from "@/core/interfaces/survey/Survey";

const {editable = true, ...props} = defineProps<{
    survey: Survey;
    section: SurveySection;
    answers: any;
    density: "compact" | "normal" | undefined;
    errors: any;
    editable?: boolean;
    /** Prefill explanations by question id, for surveys prefilled outside of an interview. */
    reasons?: Record<number, string> | null;
}>();
const emits = defineEmits(['on-change']);
const {prefilledSurveyAnswers} = storeToRefs(useInterviewStore());

const llmAnswerReasons = computed(() => {
    return prefilledSurveyAnswers.value?.reduce((acc, answer) => {
        acc[answer.question_id] = answer.reason ?? null;

        return acc;
    }, {});
});

const onCheckUpdate = (question: CheckInputSurveyQuestion, checked: boolean) => {
    if (checked && question.answers?.length) {
        emits('on-change', {label: question.answers[0].label, questionId: question.id});
    } else {
        emits('on-change', {label: "", questionId: question.id});
    }
};

/**
 * Get the answer value for a question.
 *
 * @param {Number} questionId The question id.
 *
 * @return {Array}
 */
const getAnswerValue = (questionId) => {
    return props.answers[questionId] || [];
};

/**
 * Get the LLM answer reason for a question.
 *
 * @param {Number} questionId The question id.
 *
 * @returns {string|null}
 */
const getReasonForQuestion = (questionId) => {
    if (props.reasons?.[questionId]) {
        return props.reasons[questionId];
    }

    if (!llmAnswerReasons.value) {
        return null;
    }

    return llmAnswerReasons.value[questionId] ?? null;
};
</script>

<template>
  <base-container class="gap-y-1">
    <div
      v-for="(question, index) in section.questions"
      :id="`question-${question.id}`"
      :key="question.id"
      :class="{'flex flex-row' : question.type.slug === QuestionType.CHECK_INPUT}"
      style="scroll-margin: 90px"
    >
      <div v-if="question.type.slug === QuestionType.CHECK_INPUT">
        <input
          type="checkbox"
          class="w-6 h-6 mr-3 text-blue-600 bg-gray-100 border-gray-300 rounded mt-1 focus:ring-0 focus:ring-primary cursor-pointer"
          :name="question.id.toString()"
          :checked="!!getAnswerValue(question.id)?.length"
          :disabled="!editable"
          @input="(event) => onCheckUpdate(question as CheckInputSurveyQuestion, (event.target as HTMLInputElement)?.checked)"
        >
      </div>
      <div :class="{'flex flex-col w-full' :question.type.slug === QuestionType.CHECK_INPUT}">
        <div class="flex flex-col">
          <p>
            {{ index + 1 }}. {{ question.title }}<span v-if="getReasonForQuestion(question.id)">&nbsp;*</span>
            <v-tooltip
              v-if="question.description?.length && question.type.slug === QuestionType.CHECK_INPUT"
              location="bottom"
              content-class="bg-grey-darken-2"
            >
              <template #activator="{ props }">
                <icon
                  v-bind="props" class="h-6 w-6 cursor-pointer" color="primary"
                  name="mdi-help-circle-outline"
                />
              </template>
              <markdown-view :content="question.description" class="text-white" />
            </v-tooltip>
          </p>
          <p
            v-if="!!errors && errors[question.id]"
            class="text-sm text-red-500"
          >
            {{ errors[question.id] }}
          </p>
        </div>
        <div v-if="question.description?.length">
          <label
            v-if="question.description?.length && question.type.slug !== QuestionType.CHECK_INPUT"
            class="text-sm mt-1"
          >
            <div v-for="(line, l) in question.description.split('\n')" :key="question.id + '-' + l">
              👉 {{ line }}
            </div>
          </label>
        </div>
        <p
          v-if="getReasonForQuestion(question.id)"
          class="mt-1 italic text-sm text-gray-500"
        >
          * {{ getReasonForQuestion(question.id) }}
        </p>
        <survey-built-answer
          :question="question"
          :survey-id="survey.id"
          :editable="editable"
          :density="density"
          :value="getAnswerValue(question.id)"
          class="my-2"
          @change="(change) => emits('on-change', {label: change, questionId: question.id})"
        />
        <v-divider :class="density === 'compact' ? 'mt-2' : 'mt-4'" />
      </div>
    </div>
  </base-container>
</template>
